import { RestartAlt } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import PageBody from 'components/atoms/PageBody'
import PageHeading from 'components/atoms/PageHeading'
import Rubric from 'components/molecules/Rubric'
import SubmissionInfo from 'components/molecules/SubmissionInfo'
import SubmissionStepper from 'components/utilized/assignment/SubmissionStepper'
import StudentLearningObjectivesAccordion from 'components/utilized/assignment/StudentLearningObjectivesAccordion'
import { Formik, Form } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useGET_submissionByIdQuery, usePUT_submissionRequestResubmissionMutation } from 'redux/api/submissionsApi'
import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'
import { formatDate, toHtml } from 'utils/helpers'
import formModel, { initialValues, validationSchema } from 'utils/formModels/assignmentAssessModel'
import { useGET_assignmentByIdQuery, useGET_studentAssignmentByIdQuery, usePOST_assignmentAssessMutation } from 'redux/api/assignmentsApi'
import { IAssessData } from 'types'
import { userAPI } from 'redux/api/userAPI'
import RichTextField from 'components/atoms/fields/RichTextField'
import DatePickerField from 'components/atoms/fields/DatePickerField'
import InputField from 'components/atoms/fields/InputField'
import SubmitButton from 'components/atoms/buttons/SubmitButton'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import { FaChevronLeft } from 'react-icons/fa'
import BoxTitle from 'components/atoms/BoxTitle'

const SubmissionPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const user = useSelector((state: RootState) => state['user'].user)

  const params = useParams()
  const id = params?.id

  const [activeStep, setActiveStep] = useState(0)

  const { data: submission } = useGET_submissionByIdQuery(id.toString())

  const assignmentId = submission?.assignment?.id as unknown as any ?? ""

  const { data: assignmentData } = useGET_assignmentByIdQuery(assignmentId?.toString(), 
    {skip: !assignmentId || ['parent'].includes(user?.role)})

  const { data: studentAssignmentData } = useGET_studentAssignmentByIdQuery(assignmentId?.toString(), 
  {skip: !assignmentId || !['parent'].includes(user?.role)})

  const [
    assessStudent,
    { isLoading: assessLoading, isSuccess: assessSuccess, error: assessError, data: assesseData },
  ] = usePOST_assignmentAssessMutation()
  useErrorAlert(assessError)
  useSuccessAlert(assessSuccess ? `${t('alerts.entity-saved', {entity: 'Assess data'})}` : '')

  
  const [requestResubmission, { isSuccess: resubmitSuccess, error: resubmitError, data: resubmit }] = 
    usePUT_submissionRequestResubmissionMutation(assignmentId)
    
  useErrorAlert(resubmitError)
  useSuccessAlert(resubmitSuccess ? t('alerts.resubmit-success') : '')
  


  useEffect(() => {
    if(assessSuccess){
      navigate('/submissions')
    }
  }, [assessSuccess])


  const {
    comment,
    grading_components,
    final,
    bonus_points,
    reported_time,
    observation,
    observed_at
  } = formModel()


  let serverValues = {
    final: 0,
    bonus_points: 0,
    reported_time: 0,
    comment: '',
    observation: '',
    observed_at: null as string | null,
    rubric_grades: [],
    grading_components: assignmentData ? assignmentData.grading_components.map(component => {
      return {
        ...component, 
        grade: 0
      }
    }) : []
  }

  if(submission && submission.assessment && assignmentData){

    const final_grade_value = assignmentData?.score_type?.id === 'points' 
      ? submission.assessment.final_grade_points
      : submission.assessment.final_grade_percentage 

      serverValues = {
        final: final_grade_value,
        bonus_points: submission.assessment.bonus_points ?? 0,
        reported_time: submission.assessment.reported_time ?? 0,
        comment:  submission.assessment.comment,
        observation: submission.assessment.observation ?? '',
        observed_at: submission.assessment.observed_at ?? null,
        rubric_grades: submission.assessment.rubric_grades,
        grading_components: submission.assessment.grading_components.map(component => {
          return {
            id: component.grading_component_id,
            title: component.grading_component.title, 
            grade: assignmentData?.score_type?.id === 'points' 
              ? component.grade_points
              : component.grade_percentage,
          }
        }) 
      }

  }


  const gradingStep = submission && submission.rubric ? 2 : 1
  const gradingPrevStep = submission && submission.rubric ? 1 : 0
  const steps = submission && submission.rubric ? 
    [{
      label: t('common.view-submission'),
    },
    {
      label: t('assignments.assess'),
    },
    {
      label: t('common.assess-submission'),
    }] :
        [{
          label: t('common.view-submission'),
        },
        {
          label: t('common.assess-submission'),
        }]



  return (
    <>{submission && <>
      <PageHeading title={`${t('common.submissions')} / ${submission?.assignment?.title}`} />

      <PageBody>

        {['parent'].includes(user?.role) && studentAssignmentData ? <>
        
          <Grid container spacing={4}>
                
                <Grid item xs={12} md={4}>
                  <b>{t('common.student')}</b>: {submission?.student?.title}
                </Grid>
                <Grid item xs={12} md={4}>
                  <b>{t('common.group')}</b>: {submission?.group?.title}
                </Grid>
                <Grid item xs={12} md={4}>
                  <b>{t('common.course')}</b>: {submission?.course?.title}
                </Grid>

                <Grid item xs={12} md={4}>
                  <b>{t('common.category')}</b>: {submission?.category?.title}
                </Grid>
                <Grid item xs={12} md={4}>
                  <b>{t('common.submission-type')}</b>: {submission?.submission_type}
                </Grid>
                <Grid item xs={12} md={4}>
                  <b>{t('common.assessment-type')}</b>: {submission?.assessment_type}
                </Grid>

                <Grid item xs={12} md={4}>
                  <b>{t('assignments.date-assigned')}</b>: {formatDate(submission?.date_assigned)}
                </Grid>

                <Grid item xs={12} md={4}>
                  <b>{t('common.bonus-points-active-participation')}</b>: {submission?.assessment?.bonus_points}%
                </Grid>

                <Grid item xs={12} md={4}>
                  <b>{t('common.grade')}</b>: {submission?.assessment?.final_grade_percentage}%
                </Grid>
            </Grid>

            <Box  className="thick-border" sx={{ marginTop: '20px' }}>
                  <BoxTitle title={`${t('common.assignment-instructions')}`} />

                  <Typography
                        sx={{marginTop: '20px', fontSize: '14px'}}
                        dangerouslySetInnerHTML={{
                        __html: studentAssignmentData.description,
                        }}
                      />
              </Box>


              <Box className="thick-border" sx={{ marginTop: '20px' }}>
                  <BoxTitle title={`${t('common.submission')}`} />
                  <SubmissionInfo id={submission.id} />
              </Box>


            {studentAssignmentData && submission.id && submission.rubric?.id && <Box className="thick-border" sx={{ marginTop: '20px' }}>
                  <BoxTitle title={`${t('common.assessment')}`} />
                  <div style={{ height: '20px' }} />
                  <Rubric 
                    id={submission.rubric.id} 
                    extraCredit={studentAssignmentData?.has_extra_credit ? studentAssignmentData?.extra_credit : 0}
                    rubricGrades={submission?.assessment?.rubric_grades ?? []} 
                  />
              </Box>
           }

            <div style={{ marginTop: '40px'}}>
              <Button
                    size='small'
                    variant='outlined'
                    color='neutral'
                    sx={{ marginLeft: '5px' }}
                    onClick={() => navigate('/submissions') }
                  >
                    {`${t('common.back')}`}
                  </Button>
            </div>

        </> : 

        <div style={{ display: 'flex', gap: '20px'}}>{assignmentData  &&  <>
            <SubmissionStepper steps={steps} activeStep={activeStep}/>
          
            <div>
            {activeStep==0 && <div>
            
              <Grid container spacing={4}>
                
                  <Grid item xs={12} md={4}>
                    <b>{t('common.student')}</b>: {submission?.student?.title}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <b>{t('common.group')}</b>: {submission?.group?.title}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <b>{t('common.course')}</b>: {submission?.course?.title}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <b>{t('common.category')}</b>: {submission?.category?.title}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <b>{t('common.submission-type')}</b>: {submission?.submission_type}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <b>{t('common.assessment-type')}</b>: {submission?.assessment_type}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <b>{t('assignments.date-assigned')}</b>: {formatDate(submission?.date_assigned)}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <b>{t('assignments.date-submitted')}</b>: {formatDate(submission?.date_submitted)}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Button
                      size='small'
                      variant='contained'
                      color='neutral'
                      sx={{ marginLeft: '5px' }}
                      onClick={() => requestResubmission(submission?.id?.toString())}
                    >
                      <RestartAlt />
                      {`${t('common.request-resubmission')}`}
                    </Button>
                  </Grid>


              </Grid>


              <Box  className="thick-border" sx={{ marginTop: '40px' }}>
                  <BoxTitle title={`${t('common.assignment-instructions')}`} />

                  <Typography
                        sx={{marginTop: '20px', fontSize: '14px'}}
                        dangerouslySetInnerHTML={{
                        __html: assignmentData.description,
                        }}
                      />
                      {assignmentData.grading_components?.map(g => <div key={g.id} style={{ marginTop: '10px' }}><b>&bull; {g.title}</b> 
                        <Typography sx={{ fontSize: '14px'}}
                          dangerouslySetInnerHTML={{
                            __html: g.description,
                        }}
                    /></div>)}
              </Box>

              <Box className="thick-border" sx={{ marginTop: '40px' }}>
                  <BoxTitle title={`${t('common.submission')}`} />
                  <SubmissionInfo id={submission.id} />
              </Box>

              <div style={{ marginTop: '40px', marginBottom: '100px', display: 'flex', gap: '10px'}}>
                <div>
                    <Button
                      size='small'
                      variant='outlined'
                      color='neutral'
                      onClick={() => navigate('/submissions') }
                    >
                      <FaChevronLeft />
                      {`${t('common.back')}`}
                    </Button>

                    <Button
                      sx={{marginLeft: '10px'}}
                      size='small'
                      variant='outlined'
                      color='neutral'
                      onClick={() => navigate(`/assignments/view/${assignmentData.id}`) }
                    >
                      {`${t('assignments.assignment-details')}`}
                    </Button>

                </div>
                <div style={{ marginLeft: 'auto'}}>
                  <Button
                      size='small'
                      variant='contained'
                      color='primary'
                      onClick={() => {
                          setActiveStep(1)
                      }}
                    >
                      {`${t('common.next')}`}
                    </Button>
                </div>
              </div>
            </div>}



            
                  <Formik
                    enableReinitialize
                    initialValues={serverValues}
                    validationSchema={validationSchema}
                    onSubmit={(values: any) => { 

                        const gradings = values.grading_components.map((component, index) => {
                          if (assignmentData.score_type?.id === 'points') {
                            return {
                              grading_component_id: Number(assignmentData.grading_components[index].id),
                              grade_points: component.grade && component.grade > 0 ? Number(component.grade ) : 0,
                            }
                          } else {
                            return {
                              grading_component_id: Number(assignmentData.grading_components[index].id),
                              grade_percentage: component.grade && component.grade > 0 ? Number(component.grade) : 0,
                            }
                          }
                        })

                        let studentData: any  =  {
                          teacher_id: Number(submission.assessment?.teacher_id) ?? null,
                          assignment_id: Number(assignmentId),
                          student_id: Number(submission.student.id),
                          comment: values.comment,
                          observation: values.observation,
                          observed_at: values.observed_at || null,
                          rubric_grades: values.rubric_grades,
                          grading_components: gradings,
                          bonus_points: Number(values.bonus_points),
                          reported_time: Number(values.reported_time),
                        }

                        if (assignmentData.score_type.id === 'points') studentData.final_grade_points = Number(values.final)
                        else studentData.final_grade_percentage = Number(values.final)

                        let assessmentData: IAssessData = {
                          teacher_id: Number(submission.assessment?.teacher_id) ?? null,
                          students: [studentData]
                        }

                        assessStudent({id: submission.assignment.id.toString(), data: assessmentData})
                    }}>
                    {({ values, setFieldValue }) => {

                      return (
                          <Form style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '40px' }}>

                            {activeStep > 0 && <StudentLearningObjectivesAccordion objectives={assignmentData?.student_learning_objectives} />}
                            
                            {submission.rubric && activeStep==1 && <div style={{width: '100%'}}>
                                    

                                    {submission?.rubric && <Rubric 
                                        id={submission.rubric.id} 
                                        extraCredit={assignmentData?.has_extra_credit ? assignmentData?.extra_credit : 0}
                                        rubricGrades={values?.rubric_grades ?? []} 
                                        selectHandler={(newRubricGrades: any[], points: number, percent: number) => {

                                          setFieldValue("rubric_grades", newRubricGrades)

                                          if(assignmentData?.score_type?.id === 'points')
                                            setFieldValue(final.name, points)
                                          else
                                            setFieldValue(final.name, percent)

                                        }} /> 
                                    }


                                    <div style={{ width: '100%', marginTop: '40px', marginBottom: '100px', display: 'flex', gap: '10px'}}>
                                      <div>
                                          <Button
                                            size='small'
                                            variant='outlined'
                                            color='neutral'
                                            onClick={() => setActiveStep(0) }
                                          >
                                            <FaChevronLeft />
                                            {`${t('common.back')}`}
                                          </Button>
                                      </div>
                                      <div style={{ marginLeft: 'auto'}}>
                                      <Button
                                            size='small'
                                            variant='contained'
                                            color='primary'
                                            onClick={() => {
                                                setActiveStep(2)
                                            }}
                                          >
                                            {`${t('common.next')}`}
                                          </Button>
                                      </div>
                                    </div>
                            </div>}



                            {activeStep==gradingStep && <div style={{width: '100%'}}>

                                                    <Grid container spacing={4}>
                                                      
                                                      {assignmentData.expected_time_type && <><Grid item xs={12} lg={4}>
                                                          <InputField
                                                              fieldProps={{...reported_time, label: `${reported_time.label} (${assignmentData.expected_time_type})`}}
                                                          />
                                                      </Grid>
                                                      <Grid item xs={12} lg={4}>
                                                        <InputField
                                                              fieldProps={{...bonus_points}}
                                                          />
                                                      </Grid></>}
                                                      <Grid item xs={12} lg={4}>
                                                        {
                                                              serverValues.grading_components.map((component, index) => {

                                                                return <InputField
                                                                  valueSelector='grade'
                                                                  key={'grade_component_'+component.id}
                                                                  fieldProps={{
                                                                    label: component.title,
                                                                    name: `${grading_components.name}[${index}]`,
                                                                  }}
                                                                  sx={{ marginBottom: '20px' }}
                                                                />
                                                              })
                                                          }

                                                          <InputField
                                                              fieldProps={{...final}}
                                                          />
                                                      </Grid>


                                                      <Grid item xs={12} md={12}>
                                                        <RichTextField
                                                          width={'100%'}
                                                          height={'150px'}
                                                          fieldProps={{...comment}}
                                                          label={comment.label}
                                                        />
                                                      </Grid>

                                                      <Grid item xs={12} md={12}>
                                                        <RichTextField
                                                          width={'100%'}
                                                          height={'150px'}
                                                          fieldProps={{...observation}}
                                                          label={observation.label}
                                                        />
                                                      </Grid>
                                                      <Grid item xs={12} lg={4}>
                                                        <DatePickerField fieldProps={{...observed_at}} />
                                                      </Grid>
                                                    </Grid>

                                                    
                                                    <div style={{ width: '100%', marginTop: '40px', marginBottom: '100px', display: 'flex',  gap: '10px'}}>
                                                      <div>
                                                          <Button
                                                            size='small'
                                                            variant='outlined'
                                                            color='neutral'
                                                            onClick={() => setActiveStep(gradingPrevStep) }
                                                          >
                                                            <FaChevronLeft />
                                                            {`${t('common.back')}`}
                                                          </Button>
                                                      </div>
                                                      <div style={{ marginLeft: 'auto' }}>
                                                        <SubmitButton
                                                            color={'success'}
                                                            text={`${t('common.save-assessment')}`}
                                                            isLoading={assessLoading}
                                                            alignRight={false}
                                                          />
                                                      </div>
                                                    </div>
                               </div>
                             }
                        </Form>
                      )
                    }}
                </Formik>
             
            </div> 
            </>}  
        </div>}
      </PageBody></>}
    </>
  )
}
export default SubmissionPage
